<template>
    <div
        ref="renderer"
        class="size-full bg-gray-100"
    >
        <camera-view
            ref="cameraView"
            :controller="viewController"
            :view="selectedView"
            :view-options="overrideViewOptions"
            :no-poster="true"
            @cameraLoading="cameraLoading"
            @afterSceneChanged="afterSceneChanged"
        />

        <transition name="cz3__fade">
            <group-preview v-if="groupPreviewVisible" />
        </transition>

        <transition name="cz3__fade">
            <thumbnails-list
                v-if="thumbnailsVisible"
                :thumbnails="thumbnails"
                @selected="selectView"
            />
        </transition>
    </div>
</template>

<script>
    import CameraView from './Three/CameraView.vue';

    import ThumbnailsList from './ThumbnailsList.vue';
    import GroupPreview from './GroupPreview.vue';

    // eslint-disable-next-line no-unused-vars
    import * as constants from '../constants';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            CameraView,
            ThumbnailsList,
            GroupPreview,
        },

        data() {
            return {
                previews: {},
            };
        },

        computed: {
            currentStep() {
                return this.appStore.currentStep;
            },

            thumbnailsVisible() {
                return true; // this.currentStep !== constants.STEP_REVIEW;
            },

            groupPreviewVisible() {
                return this.customizerStore.groupPersonalization?.length > 1;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            selectedView() {
                return this.customizerStore.selectedView;
            },

            selectedViewCounter() {
                return this.customizerStore.selectedViewCounter;
            },

            overrideViewOptions() {
                return {};
            },

            thumbnails() {
                const list = [];

                if (this.viewController == null) {
                    return list;
                }

                for (const view of this.viewController.blueprint.views) {
                    const preview = this.previews[view.code];

                    if (preview == null) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    list.push({
                        code: view.code,
                        label: view.name,
                        selected: view.code === this.selectedView?.code,
                        preview,
                    });
                }

                return list;
            },
        },

        watch: {
            viewController(newController, oldController) {
                this.captureController(newController, oldController);
            },

            selectedViewCounter() {
                if (this.selectedView && this.$refs.cameraView) {
                    this.$refs.cameraView.selectView();
                }
            },
        },

        mounted() {
            this.resize();

            this.captureController();
        },

        beforeUnmount() {
        },

        updated() {
        },

        beforeUpdate() {
        },

        methods: {
            /**
             * When a new controller is selected, capture initial parameters.
             */
            captureController(newController, oldController) {
                if (oldController) {
                    oldController.off('.renderer');
                }

                if (this.viewController) {
                    this.customizerStore.selectView(this.viewController?.blueprint?.views?.[0]);

                    this.viewController.on('stateChanged.renderer', this.stateChanged);
                } else {
                    this.customizerStore.selectView(null);
                }
            },

            /**
             * Resize the view.
             */
            resize() {
            },

            /**
             * Do stuff when controller state changed.
             */
            stateChanged() {
                this.makeThumbnails();
            },

            /**
             * Watch for loading events.
             */
            cameraLoading(status) {
                if (!status) {
                    // Done loading.
                    this.makeThumbnails();
                }
            },

            /**
             * Make thumbnails after scene updates too.
             */
            afterSceneChanged({ animating }) {
                this.animating = animating;

                this.makeThumbnails();
            },

            /**
             * Make thumbnails.
             */
            makeThumbnails() {
                if (window.innerWidth < 1024) {
                    // No thumbnails on mobile.
                    return;
                }

                if (this.customizerStore.suppressThumbnails) {
                    return;
                }

                // Debounce.
                if (this.makeThumbnailsTimer) {
                    clearTimeout(this.makeThumbnailsTimer);
                }

                this.makeThumbnailsTimer = setTimeout(this.makeThumbnailsDebounced, 100);
            },

            /**
             * Make thumbnails after everything settled.
             */
            async makeThumbnailsDebounced() {
                if (this.$refs.cameraView == null) {
                    return;
                }

                if (this.viewController == null) {
                    return;
                }

                // If animations are running, defer till later.
                if (this.animating) {
                    this.makeThumbnails();
                    return;
                }

                for (const view of this.viewController.blueprint.views) {
                    if (view.viewType === 'Camera') {
                        // eslint-disable-next-line no-await-in-loop, no-unused-vars
                        const thumbnail = await this.snapshotView(view);

                        this.previews[view.code] = thumbnail;
                    }
                }
            },

            /**
             * Snapshot a single view.
             */
            // eslint-disable-next-line no-unused-vars
            async snapshotView(view) {
                return new Promise((resolve) => {
                    // Snapshot this view.
                    // eslint-disable-next-line no-await-in-loop
                    this.$refs.cameraView.snapshot({
                        // What to snapshot.
                        controllerId: this.viewController.id,
                        view: view.code,

                        // We want thumbnails of a very specific size.
                        overrideWidth: 600,
                        overrideHeight: 600,

                        // Hide ground.
                        overrideHideGround: true,

                        // And make the background clear.
                        overrideBackground: '#00000000',

                        mimeType: 'image/png',

                        // Callback.
                        done: (image) => {
                            resolve(image);
                        },
                    });
                });
            },

            /**
             * Handle view selection.
             */
            selectView(viewCode) {
                const view = this.viewController.blueprint.views.find((x) => x.code === viewCode);

                if (view) {
                    this.customizerStore.selectView(null);
                    this.$nextTick(() => {
                        this.customizerStore.selectView(view);
                    });
                }
            },
        },
    };
</script>
